import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, CheckCircle2, Circle, SkipForward, ExternalLink } from "lucide-react";
import api, { mediaUrl } from "../lib/api";
import { ukDate } from "../lib/dates";
import { SermonNotes, markWatched } from "../components/SermonNotes";

const ytId = (url = "") => (url.match(/(?:youtube\.com\/(?:watch\?v=|embed\/|live\/|shorts\/|v\/)|youtu\.be\/)([\w-]{11})/) || [])[1];

const Player = ({ s }) => {
  const vid = s.youtube_video_id || ytId(s.source_url);
  if (vid) return <iframe data-testid="series-player" title={s.title} src={`https://www.youtube.com/embed/${vid}?autoplay=1`} allow="autoplay; encrypted-media; picture-in-picture" allowFullScreen className="w-full aspect-video rounded-2xl bg-black" />;
  if ((s.content_type || "").startsWith("audio")) return <audio data-testid="series-player" src={mediaUrl(s.id)} controls autoPlay className="w-full" />;
  if ((s.content_type || "").startsWith("video")) return <video data-testid="series-player" src={mediaUrl(s.id)} controls autoPlay className="w-full rounded-2xl" />;
  return <a data-testid="series-player" href={s.source_url || mediaUrl(s.id)} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 text-sm font-semibold" style={{ color: "var(--sj-primary)" }}><ExternalLink className="h-4 w-4" /> Open sermon</a>;
};

export default function SermonSeries() {
  const { name } = useParams();
  const [data, setData] = useState(null);
  const [idx, setIdx] = useState(0);
  const [error, setError] = useState("");

  useEffect(() => {
    api.get(`/sermons/series/${encodeURIComponent(name)}`).then(({ data }) => {
      setData(data);
      const first = data.parts.findIndex((p) => !p.watched);
      setIdx(first === -1 ? 0 : first);
    }).catch(() => setError("This series couldn't be found."));
  }, [name]);

  const parts = data?.parts || [];
  const cur = parts[idx];
  useEffect(() => {
    if (!cur || cur.watched) return;
    markWatched(cur.id);
    setData((d) => ({ ...d, parts: d.parts.map((p) => (p.id === cur.id ? { ...p, watched: true } : p)) }));
  }, [cur]);

  if (error) return <div className="max-w-4xl mx-auto p-10 text-center" data-testid="series-error">{error} <Link to="/sermons" className="underline">Back to sermons</Link></div>;
  if (!cur) return null;
  const watched = parts.filter((p) => p.watched).length;
  return (
    <div className="max-w-6xl mx-auto space-y-6" data-testid="series-page">
      <Link to="/sermons" data-testid="series-back" className="inline-flex items-center gap-1.5 text-sm font-semibold" style={{ color: "var(--sj-primary)" }}><ArrowLeft className="h-4 w-4" /> Sermon library</Link>
      <div>
        <div className="text-[11px] uppercase tracking-[0.18em] font-semibold" style={{ color: "var(--sj-primary)" }}>Series</div>
        <h1 className="font-head text-3xl sm:text-4xl font-extrabold tracking-tight" data-testid="series-title">{data.name}</h1>
        <p className="text-sm mt-1" style={{ color: "var(--sj-text-2)" }} data-testid="series-progress">{watched} of {parts.length} watched</p>
      </div>
      <div className="grid lg:grid-cols-[minmax(0,1.7fr)_minmax(0,1fr)] gap-6">
        <div className="min-w-0">
          <Player key={cur.id} s={cur} />
          <div className="mt-4 flex flex-wrap items-start justify-between gap-3">
            <div className="min-w-0">
              <div className="text-xs font-semibold" style={{ color: "var(--sj-secondary)" }}>Part {idx + 1} of {parts.length}</div>
              <h2 className="font-head text-xl font-bold" data-testid="series-current-title">{cur.title}</h2>
              <div className="text-sm" style={{ color: "var(--sj-text-2)" }}>{[cur.speaker, cur.sermon_date && ukDate(cur.sermon_date)].filter(Boolean).join(" · ")}</div>
            </div>
            {idx < parts.length - 1 && (
              <button data-testid="series-next" onClick={() => setIdx(idx + 1)} className="inline-flex items-center gap-1.5 rounded-full px-5 py-2 text-sm font-semibold text-white hover:brightness-105" style={{ background: "var(--sj-primary)" }}>Next part <SkipForward className="h-4 w-4" /></button>
            )}
          </div>
          <SermonNotes sermonId={cur.id} />
        </div>
        <ol className="bg-white rounded-2xl border border-[#EBE9E4] divide-y divide-[#EBE9E4] self-start" data-testid="series-parts">
          {parts.map((p, i) => (
            <li key={p.id}>
              <button data-testid={`series-part-${i + 1}`} onClick={() => setIdx(i)} className={`w-full text-left px-4 py-3 flex items-start gap-3 hover:bg-[#F4F8F6] ${i === idx ? "bg-[#E3F1EC]" : ""}`}>
                {p.watched ? <CheckCircle2 className="h-5 w-5 shrink-0" style={{ color: "var(--sj-secondary)" }} /> : <Circle className="h-5 w-5 shrink-0" style={{ color: "var(--sj-text-3)" }} />}
                <span className="min-w-0">
                  <span className="block text-sm font-semibold truncate">{i + 1}. {p.title}</span>
                  <span className="block text-xs" style={{ color: "var(--sj-text-3)" }}>{p.sermon_date ? ukDate(p.sermon_date) : ""}</span>
                </span>
              </button>
            </li>
          ))}
        </ol>
      </div>
    </div>
  );
}
